import React from 'react';
import { Paper, Typography, Box, Grid, Chip, Stack } from '@mui/material';
import { LocationOn, LocalShipping, Receipt } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

import { useGetCustomerAddresses } from '../../../hooks/customers/useGetCustomerAddresses';
import { countries } from '../../../constants/countres';

const countryName = (code) => {
    if (!code) return '';
    const c = countries.find((x) => x.code === code);
    return c ? c.label : code;
};

const AddressCard = ({ address, isArabic }) => {
    const lines = [
        address.street,
        address.building && `${isArabic ? 'مبنى' : 'Bldg'} ${address.building}`,
        address.floor && `${isArabic ? 'دور' : 'Floor'} ${address.floor}`,
        address.apartment && `${isArabic ? 'شقة' : 'Apt'} ${address.apartment}`,
        address.area,
        address.city,
        countryName(address.country_code),
    ].filter(Boolean);

    return (
        <Paper
            elevation={0}
            sx={{
                p: 2.5,
                borderRadius: 3,
                border: (t) => `1px solid ${t.palette.divider}`,
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                gap: 1,
            }}
        >
            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={0.5}>
                <Stack direction="row" spacing={1} alignItems="center">
                    <LocationOn fontSize="small" color="primary" />
                    <Typography variant="subtitle2" fontWeight={700}>
                        {address.label || (isArabic ? 'عنوان' : 'Address')}
                    </Typography>
                </Stack>
                <Stack direction="row" spacing={0.5}>
                    {address.is_default_shipping && (
                        <Chip
                            size="small"
                            icon={<LocalShipping sx={{ fontSize: 14 }} />}
                            label={isArabic ? 'شحن افتراضي' : 'Default Shipping'}
                            color="primary"
                            variant="tonal"
                            sx={{ fontWeight: 'bold', borderRadius: '6px' }}
                        />
                    )}
                    {address.is_default_billing && (
                        <Chip
                            size="small"
                            icon={<Receipt sx={{ fontSize: 14 }} />}
                            label={isArabic ? 'فاتورة افتراضية' : 'Default Billing'}
                            color="success"
                            variant="tonal"
                            sx={{ fontWeight: 'bold', borderRadius: '6px' }}
                        />
                    )}
                </Stack>
            </Stack>

            {address.formatted_address ? (
                <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-line' }}>
                    {address.formatted_address}
                </Typography>
            ) : (
                <Stack spacing={0.25}>
                    {lines.map((line, i) => (
                        <Typography key={i} variant="body2" color="text.secondary">
                            {line}
                        </Typography>
                    ))}
                </Stack>
            )}

            {address.phone && (
                <Typography variant="caption" color="text.secondary" sx={{ direction: 'ltr', mt: 'auto' }}>
                    {isArabic ? 'هاتف:' : 'Phone:'} {address.phone}
                </Typography>
            )}
        </Paper>
    );
};

const CustomerAddressesTab = ({ customerId }) => {
    const { i18n } = useTranslation();
    const isArabic = (i18n.language || 'en').startsWith('ar');

    const { addresses, isLoading } = useGetCustomerAddresses(customerId);

    if (isLoading) {
        return (
            <Box sx={{ p: 3, textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                    {isArabic ? 'جاري التحميل...' : 'Loading...'}
                </Typography>
            </Box>
        );
    }

    if (addresses.length === 0) {
        return (
            <Paper
                elevation={0}
                sx={{
                    p: 4,
                    borderRadius: 3,
                    border: (t) => `1px dashed ${t.palette.divider}`,
                    textAlign: 'center',
                }}
            >
                <LocationOn sx={{ fontSize: 36, color: 'text.disabled', mb: 1 }} />
                <Typography variant="body2" color="text.secondary">
                    {isArabic ? 'لا توجد عناوين محفوظة' : 'No addresses saved yet'}
                </Typography>
            </Paper>
        );
    }

    return (
        <Grid container spacing={2}>
            {addresses.map((addr) => (
                <Grid key={addr.id} item xs={12} sm={6} md={4}>
                    <AddressCard address={addr} isArabic={isArabic} />
                </Grid>
            ))}
        </Grid>
    );
};

export default CustomerAddressesTab;
